import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { tr } from "@/lib/tr";

/* ⚡ Auto-play burst — same flow as Penalty's AUTO-KICK: tap a side once and
   ×3/×5/×10/×20 plays fire back-to-back. Works with the 🔁 Recovery pills (the
   hub raises after a loss / resets on a win) and auto-stops after 7 losses
   in a row. A finished ×20 session locks ALL bursts for 60 minutes. */
/* shared flag the ArcadeHub reads: while a burst is running,
   Penalty-style loss recovery applies automatically (no pill needed) */
export const burstFlag = { active: false, endHook: null, halt: false };

/* 🧊 PER-GAME 60-min burst cooldown after a ×20 session — only locks the game
   that finished ×20. Lives in sessionStorage so a fresh app open never shows
   a stale "session break" from a previous visit. */
/* 🎓 demo accounts practice freely — no cooldowns/caps (set by ArcadeHub) */
export const burstDemoFree = { on: false };

const CD_PREFIX = "vts_burst_cd2_";
try {
  localStorage.removeItem("vts_burst_cd_until");
  // clear v1 per-game keys that could have been set by an aborted ×20 (0 plays)
  Object.keys(sessionStorage).filter((k) => k.startsWith("vts_burst_cd_")).forEach((k) => sessionStorage.removeItem(k));
} catch { /* one-time cleanup of old keys */ }
export const burstCooldownLeft = (gameId) => {
  if (burstDemoFree.on) return 0;
  try { return Math.max(0, (parseInt(sessionStorage.getItem(CD_PREFIX + gameId), 10) || 0) - Date.now()); } catch { return 0; }
};
export const startBurstCooldown = (gameId, mins = 60) => {
  if (burstDemoFree.on) return;
  try { sessionStorage.setItem(CD_PREFIX + gameId, String(Date.now() + mins * 60000)); } catch { /* ignore */ }
};
export const cooldownToast = (gameId) => {
  toast.info(tr("burst_cd_title", { mins: Math.ceil(burstCooldownLeft(gameId) / 60000) }),
    { description: tr("burst_cd_desc"), duration: 4500 });
};

/* self-ticking countdown chip shown while the ×20 cooldown is active */
export const BurstCooldownChip = ({ id }) => {
  const [left, setLeft] = useState(burstCooldownLeft(id));
  useEffect(() => {
    const t = setInterval(() => setLeft(burstCooldownLeft(id)), 1000);
    return () => clearInterval(t);
  }, [id]);
  if (left <= 0) return null;
  const mm = String(Math.floor(left / 60000)).padStart(2, "0");
  const ss = String(Math.floor((left % 60000) / 1000)).padStart(2, "0");
  return (
    <span data-testid={`${id}-burst-cooldown`}
      className="px-2.5 h-9 inline-flex items-center rounded-lg bg-[#53b9ff]/10 border border-[#53b9ff]/40 text-[#53b9ff] text-[11px] font-mono font-black">
      🧊 {mm}:{ss}
    </span>
  );
};

export const useAutoRun = (gameId = "arcade") => {
  const [runs, setRuns] = useState(1);
  const [left, setLeft] = useState(0);
  const [active, setActive] = useState(false);
  const [prog, setProg] = useState(null); // 📊 live burst progress {done,n,w,l,net}
  const stopRef = useRef(false);
  const busyRef = useRef(false);
  const runsRef = useRef(1);
  runsRef.current = runs;

  const burst = useCallback(async (play, ...args) => {
    if (busyRef.current) return;
    const n = runsRef.current;
    if (n > 1 && burstCooldownLeft(gameId) > 0) { cooldownToast(gameId); return; }
    busyRef.current = true;
    stopRef.current = false;
    setActive(true);
    burstFlag.active = n > 1;
    burstFlag.halt = false;
    // 🛑 loss auto-stop follows the planned ladder depth — never one play
    // before the final recovery shot (default 7 without a deeper plan)
    let stopAt = 5; // default cap 4 → pause after the full ladder (cap + 1 plays)
    try {
      const dpt = parseInt(localStorage.getItem("vts_arcade_recov_cap"), 10);
      if (dpt >= 3 && dpt <= 6) stopAt = dpt + 1;
    } catch { /* default */ }
    let lossRow = 0;
    let w = 0, l = 0, net = 0;
    if (n > 1) setProg({ done: 0, n, w: 0, l: 0, net: 0 });
    try {
    for (let i = 0; i < n; i += 1) {
      setLeft(n - i);
      // 🎯 getter args re-resolve every play, so signal bursts follow the freshest best signal
      const resolved = args.map((a) => (typeof a === "function" ? a() : a));
      const res = await play(...resolved); // eslint-disable-line no-await-in-loop
      if (!res) {
        // ⚠️ play didn't fire (trade error / lock) — abort burst, NO break applied
        if (w + l === 0) toast.error(tr("burst_abort_title"), { description: tr("burst_abort_desc"), duration: 5000 });
        break;
      }
      if (stopRef.current) break;
      if (res.won) w += 1; else l += 1;
      net = Math.round((net + (res.profit || 0)) * 100) / 100;
      if (n > 1) setProg({ done: w + l, n, w, l, net });
      // 🛑 full recovery ladder lost — the engine paused itself, never auto-restart
      if (burstFlag.halt) { burstFlag.halt = false; break; }
      lossRow = res.won ? 0 : lossRow + 1;
      if (n > 1 && lossRow >= stopAt) {
        toast.info(tr("autostop_title"), { description: tr("autostop_desc") });
        break;
      }
      if (i < n - 1) await new Promise((r) => setTimeout(r, 700)); // eslint-disable-line no-await-in-loop
    }
    if (n >= 50 && w + l >= n) {
      // 🧊 break ONLY after a full completed season — an aborted run never locks anything
      startBurstCooldown(gameId, 60);
      toast.success(tr("x30_done_title", { w, l, net }), {
        description: burstDemoFree.on ? tr("x30_done_demo") : tr("x30_done_desc"), duration: 6000,
      });
    }
    } finally {
      burstFlag.active = false;
      if (n > 1 && burstFlag.endHook) burstFlag.endHook(); // single taps keep the recovery hole alive
      setLeft(0);
      setActive(false);
      // 🏁 hold the final score on screen briefly so the total is readable
      if (n > 1 && w + l > 0) {
        setProg({ done: w + l, n, w, l, net });
        setTimeout(() => setProg(null), 2500);
      } else {
        setProg(null);
      }
      busyRef.current = false;
    }
  }, [gameId]);

  const stop = useCallback(() => { stopRef.current = true; }, []);
  return { runs, setRuns, burst, stop, left, active, prog };
};

/* 📊 live burst progress — kick counter + running profit bar (shared by every
   game's AutoRunBar and Penalty's own loop) */
export const BurstProgress = ({ prog, noun = "plays", id = "burst" }) => {
  if (!prog || !prog.n) return null;
  const pct = Math.min(100, Math.round((prog.done / prog.n) * 100));
  const up = prog.net >= 0;
  return (
    <div data-testid={`${id}-burst-progress`} className="w-full space-y-1 rounded-lg border border-white/10 bg-[#0B0E14] px-2.5 py-2">
      <div className="flex items-center justify-between gap-2 text-[10px] font-mono font-black">
        <span className="text-[#F7B731]">⚡ {prog.done}/{prog.n} {noun}</span>
        <span className="text-white/55">✅ {prog.w} · ❌ {prog.l}</span>
        <span data-testid={`${id}-burst-net`} className={up ? "text-[#089981]" : "text-[#F23645]"}>
          {up ? "+" : "−"}${Math.abs(prog.net).toFixed(2)}
        </span>
      </div>
      <div className="h-2 w-full rounded-full bg-white/10 overflow-hidden">
        <div className={`h-full rounded-full transition-[width] duration-500 ${up ? "bg-[#089981]" : "bg-[#F23645]"}`}
          style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
};

export const AutoRunBar = ({ id, auto, noun = "plays" }) => {
  const [cdLeft, setCdLeft] = useState(burstCooldownLeft(id));
  useEffect(() => {
    const t = setInterval(() => setCdLeft(burstCooldownLeft(id)), 1000);
    return () => clearInterval(t);
  }, [id]);
  return (
    <div className="space-y-1">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[10px] font-black font-mono tracking-[0.25em] text-[#F7B731]">⚡ AUTO-PLAY</span>
        {[["OFF", 1], ["×3", 3], ["×5", 5], ["×10", 10], ["×20", 20], ["×50", 50], ["×100", 100]].map(([lbl, v]) => (
          <button key={v} data-testid={`${id}-autorun-${v}`} onClick={() => auto.setRuns(v)}
            disabled={auto.active || (v > 1 && cdLeft > 0)}
            className={`px-3 h-9 rounded-lg text-[12px] font-mono font-black transition-colors disabled:opacity-50 ${auto.runs === v
              ? "bg-[#2962FF] text-white"
              : "bg-[#131722] border border-white/15 text-white/55 hover:border-white/35"}`}>
            {lbl}
          </button>
        ))}
        <BurstCooldownChip id={id} />
        {auto.active && auto.left > 0 && (
          <button data-testid={`${id}-autorun-stop`} onClick={auto.stop}
            className="px-3 h-9 rounded-lg bg-[#F23645]/15 border border-[#F23645]/60 text-[#F23645] text-[11px] font-mono font-black animate-pulse">
            ■ STOP · {auto.left} left
          </button>
        )}
      </div>
      {auto.prog && <BurstProgress prog={auto.prog} noun={noun} id={id} />}
      <p className="text-[9px] font-mono text-white/40 leading-snug">
        {cdLeft > 0
          ? `🧊 session break — you finished a full session in THIS game, its auto-play unlocks when the timer ends · single ${noun} still work · other arcade games stay open`
          : auto.runs > 1
            ? `tap a side once → ${auto.runs} ${noun} fire back-to-back · a loss auto-raises ONLY the next play to win losses back plus your recovery target (like Penalty · capped at your planned recovery multiple) · a win drops straight back to base stake · every new burst starts FRESH at base · if the FULL ladder loses, the bot pauses and will NOT restart by itself${auto.runs >= 50 ? ` · finishing ×${auto.runs} starts a 60-min auto-play break` : ""} · ⚠ Stakes increase after each loss. Depth does not change the odds. A long losing streak can lose a large part or all of your balance. Real-money mode can lose your whole stake.`
            : `off — each tap fires one ${noun.replace(/s$/, "")} · pick ×3 / ×5 / ×10 / ×20 for a burst, or ×50 / ×100 for a full season (then a 60-min break) — Penalty-style auto-recovery built in`}
      </p>
    </div>
  );
};
